/**
 * Per-field TEXT BUDGETS for a recognition card — the UI slot each model-authored
 * string is written into, expressed in characters.
 *
 * WHY. The card is read before the candidate can act. The reading column is ~34rem
 * (≈62 characters per line on desktop, ≈40 at 390 px), so every unbudgeted sentence
 * the model adds pushes the affordance further down the page. A live interview card
 * measured ~1000 characters before the first tap. Length is therefore a PRODUCT
 * CONSTRAINT, not a rendering accident: the model is told the slot it writes into,
 * and an over-long card is REJECTED here rather than silently mis-rendered.
 *
 * WHY A NON-THROWING PREDICATE, not a `.narrow()` on `ProbeCardDraft`. Identical
 * reasoning to `cardStructuralGap` (see `card-structure.ts`): the probe's injected
 * `validate` is a hard trust boundary — a throw there makes `makeLlmProbeProvider`
 * fall STRAIGHT to the synthetic seed with NO model retries. Budgets are exactly the
 * kind of miss the model fixes when re-asked, so they belong in the grade-and-
 * regenerate loop. `CardTextBudget` is a real ArkType validator (the budgets have one
 * runtime source of truth and are enforced, never hand-rolled); `cardBudgetGap` runs
 * it in its NON-throwing form so the caller can regenerate instead of degrading.
 *
 * TRUNCATION IS NOT AN OPTION: cutting German prose mid-sentence ships broken copy.
 * The failure ladder is regenerate (bounded) → the deterministic seed card.
 */

import { type } from "arktype"

/**
 * The budgets, in characters, keyed by the field they bound. The ArkType definitions
 * below repeat these as literals (ArkType parses string definitions at type level, so
 * an interpolated bound would erase inference); `__tests__/card-budget.test.ts` pins
 * the two together behaviourally — a boundary-length string passes, one more fails.
 * Prompts interpolate THESE constants, so the instruction can never drift from the gate.
 */
export const CARD_TEXT_BUDGET = {
  /** Eyebrow naming the mode — one short word or phrase above the question. */
  eyebrow: 24,
  /** The question itself — one line desktop, two at 390 px. */
  prompt: 60,
  /** The affordance hint under the question ("Wähle die Seite, die dir näher ist"). */
  sublegend: 70,
  /** vignette / simulation: the framed situation — two lines desktop, four at 390 px. */
  scenario: 140,
  /** why_ladder: the surface stance the probe climbs up from. */
  seed: 140,
  /** An option row's HEADLINER — the bold, title-like line that IDENTIFIES the option.
   *  Two to four German words (»Gestaltungsspielraum«, »Verlässliche Vergütung«). The
   *  row shares its width with the two control columns, so the label column is only ~45
   *  characters wide on desktop and ~18 at 390 px; a headliner this short always sets
   *  on one line and lets the eye scan the option set before reading any of it. */
  optionLabel: 30,
  /** The SUPPORTING line under the headliner — one short sentence saying what the
   *  headliner means for this candidate. It carries the content the headliner cannot
   *  (the headliner alone is the »bare label« defect); at ~90 characters it is two lines
   *  in the narrow option column and never becomes a second paragraph. */
  optionDetail: 90,
  /** A semantic-differential pole — rendered in a narrow (~4rem) column. */
  axisPole: 16,
  /** The disclosed "Überlegung", behind a disclosure. */
  denkweise: 200,
  /** The VISIBLE voice above the card — the FIRST prose the candidate reads, ahead of
   *  the scenario and the question. Two short sentences. It rides on the decision, not
   *  the card body, so the gate resolves it the way the fold does (see `cardBudgetGap`). */
  rationale: 160,
} as const

/**
 * An option row is TWO-TIER: a short bold `label` (the headliner that identifies the
 * option) plus one short `detail` line beneath it (what the headliner means here).
 *
 * This is NOT the pre-budget shape restored. That shape was a LONG label (a full
 * first-person sentence) followed by a `detail` that restated it — two readings of the
 * same content, which is why `detail` was cut to zero. The two tiers now carry DIFFERENT
 * content at different weights: the headliner is scannable (2–4 words, ~30 chars), the
 * supporting line is the one sentence that earns it (~90 chars). Total per row (~120)
 * stays below the old label-only ceiling of 60 plus a restating detail.
 *
 * Every budgeted field also admits `undefined`. An optional ArkType key means ABSENT,
 * but the card reaching this gate has passed through `normalizeStrictOutput` and
 * `repairCardStructure` — the latter deliberately writes `options: undefined` when it
 * migrates a pairwise's poles — so present-but-undefined is a NORMAL shape here and
 * must never read as an over-budget string. (Same class of trap as the strict-output
 * nulls; see `probe.ts`.)
 */
const BudgetedOption = type({
  "label?": "string <= 30 | undefined",
  "detail?": "string <= 90 | undefined",
})

/**
 * The budgeted projection of a card's authored text. Every key is OPTIONAL — this
 * validator bounds LENGTH only; which fields a primitive must carry is
 * `cardStructuralGap`'s job, and the two stay independent so neither re-states the
 * other. Undeclared keys (id, primitive, …) pass through untouched.
 */
export const CardTextBudget = type({
  "rationale?": "string <= 160 | undefined",
  "eyebrow?": "string <= 24 | undefined",
  "prompt?": "string <= 60 | undefined",
  "sublegend?": "string <= 70 | undefined",
  "scenario?": "string <= 140 | undefined",
  "seed?": "string <= 140 | undefined",
  "denkweise?": "string <= 200 | undefined",
  "options?": BudgetedOption.array().or("undefined"),
  "lanes?": BudgetedOption.array().or("undefined"),
  "optionA?": BudgetedOption.or("undefined"),
  "optionB?": BudgetedOption.or("undefined"),
  "axis?": type({
    "left?": "string <= 16 | undefined",
    "right?": "string <= 16 | undefined",
  }).or("undefined"),
})

/**
 * The reason a card overruns its UI slots, or `null` when every authored string fits.
 * Non-throwing by construction (ArkType's functional form returns `ArkErrors`), so the
 * caller decides the consequence — the probe regenerates, it never truncates.
 *
 * `rationale` needs the second argument. It is the most prominent prose on the card (it
 * sits ABOVE the scenario and the question) but it does NOT ride on the card body: the
 * model returns it at the TOP level of its move and the platform provider stamps it, so
 * `ProbeCardDraft` omits it. Callers pass the decision-level value; the resolution here
 * mirrors the fold's exactly (`out.rationale ?? out.card?.rationale`, `actions.ts:183`),
 * so the gate measures the string the candidate actually reads. No event shape moves —
 * `ElicitationCard.rationale` was already there, it was only unbudgeted.
 */
export function cardBudgetGap(card: unknown, rationale?: unknown): string | null {
  const body = (card ?? {}) as Record<string, unknown>
  const effective = rationale ?? body["rationale"]
  const out = CardTextBudget(effective === undefined ? body : { ...body, rationale: effective })
  return out instanceof type.errors ? out.summary : null
}

/** The budgets as prompt-ready English instruction lines — ONE source of truth shared by
 *  the interview persona and the per-call scaffold, so the stated limit is the enforced one. */
export const CARD_TEXT_BUDGET_INSTRUCTIONS = [
  `\`prompt\` (the question) — MAX ${CARD_TEXT_BUDGET.prompt} characters. One sentence. It is the line the candidate reads first; anything longer wraps past the answer.`,
  `\`scenario\` (vignette/simulation only) — MAX ${CARD_TEXT_BUDGET.scenario} characters. Two short sentences of situation, no scene-setting prose.`,
  `\`seed\` (why_ladder only) — MAX ${CARD_TEXT_BUDGET.seed} characters.`,
  `EVERY option row is TWO-TIER — a bold HEADLINER (\`label\`) plus ONE supporting line (\`detail\`). Author BOTH, always, for \`options[]\`, \`optionA\` and \`optionB\`. They must carry DIFFERENT content: the detail must never restate the headliner in other words.`,
  `\`options[].label\` / \`optionA.label\` / \`optionB.label\` (the HEADLINER) — MAX ${CARD_TEXT_BUDGET.optionLabel} characters each. Two to four German words, title-like, no sentence and no full stop (»Gestaltungsspielraum«, »Verlässliche Vergütung«, »Wirkung vor Sicherheit«). It is what the candidate scans; it must name the option, not describe it.`,
  `\`options[].detail\` / \`optionA.detail\` / \`optionB.detail\` (the SUPPORTING line) — MAX ${CARD_TEXT_BUDGET.optionDetail} characters each. ONE short sentence, first person where it fits, saying what the headliner means concretely for this candidate (»Ich entscheide selbst, woran ich arbeite.«). One sentence only — never two, never a paragraph.`,
  `\`axis.left\` / \`axis.right\` (semantic_differential poles) — MAX ${CARD_TEXT_BUDGET.axisPole} characters each. They render in a narrow column: one or two words.`,
  `\`eyebrow\` — MAX ${CARD_TEXT_BUDGET.eyebrow} characters.`,
  `\`sublegend\` — MAX ${CARD_TEXT_BUDGET.sublegend} characters, and only when it tells the candidate HOW to answer. NEVER write reassurance boilerplate ("Es gibt keine richtige Antwort", "Nimm dir Zeit", "Antworte ehrlich") — the interface already says that, and it costs the candidate a full line.`,
  `\`denkweise\` — MAX ${CARD_TEXT_BUDGET.denkweise} characters. It sits behind a disclosure; two tight sentences.`,
  `\`rationale\` — MAX ${CARD_TEXT_BUDGET.rationale} characters. Two short sentences at most. It is the FIRST thing the candidate reads, above the situation and the question, so it must earn its lines: say what you now want to understand, and stop.`,
].join("\n")
